import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from "recharts";

export default function BlockCards({ blocks, selectedHeight }) {
  const cards = useMemo(() => (Array.isArray(blocks) ? blocks : []), [blocks]);

  const selected = useMemo(() => {
    if (!cards.length) return null;
    if (selectedHeight != null) {
      const found = cards.find((b) => Number(b.blockHeight) === Number(selectedHeight));
      if (found) return found;
    }
    return cards[cards.length - 1];
  }, [cards, selectedHeight]);

  const short6 = (addr) => {
    const a = String(addr || "");
    return a.length > 6 ? a.slice(0, 6) + "..." : a;
  };

  const shortAddress = (addr) => {
    const value = String(addr || "");
    return value.length > 14 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
  };

  const yData = useMemo(() => {
    const arr = Array.isArray(selected?.topAddresses) ? selected.topAddresses : [];
    return arr.map((it) => {
      const raw = String(it.address || "");
      const label = short6(raw);
      return { address: raw, label, count: Number(it.count) };
    });
  }, [selected]);

  const yAxisWidth = useMemo(() => {
    const maxLen = yData.reduce((m, d) => Math.max(m, String(d.label || '').length), 0);
    const px = maxLen * 8;
    return Math.min(Math.max(px, 56), 200);
  }, [yData]);

  if (!selected) return null;

  const chartHeight = Math.max((selected.topAddresses || []).length * 30, 120);
  const sumAddresses = Number(selected.blockSumAddr);
  const sumUtxos = Number(selected.blockSumUTXO);

  return (
    <div className="single-card-wrapper">
      <div key={selected.blockHeight} className="block-card horizontal slide-in">
        <div className="block-card-header text-lg font-semibold">Block #{selected.blockHeight}</div>
        <div className="block-stats">
          <div className="stat">
            <div className="stat-label">Sum Addresses</div>
            <div className="stat-value">{Number.isFinite(sumAddresses) ? sumAddresses.toLocaleString() : "--"}</div>
          </div>
          <div className="stat">
            <div className="stat-label">Sum UTXOs</div>
            <div className="stat-value">{Number.isFinite(sumUtxos) ? sumUtxos.toLocaleString() : "--"}</div>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={yData}
              layout="vertical"
              margin={{ top: 8, right: 8, bottom: 8, left: 4 }}
              barCategoryGap={yData.length <= 3 ? "10%" : yData.length <= 5 ? "20%" : "30%"}
            >
              <CartesianGrid vertical={false} horizontal={false} />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} />
              <YAxis dataKey="label" type="category" tick={{ fill: "#e2e8f0", fontSize: 12 }} width={yAxisWidth} interval={0} tickMargin={2} />
              <Tooltip
                labelFormatter={(label, payload) => {
                  const full = payload && payload[0] && payload[0].payload && payload[0].payload.address ? payload[0].payload.address : label;
                  return (
                    <div style={{ maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-all' }}>{full}</div>
                  );
                }}
                formatter={(value) => [value, 'UTXO']}
                contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", color: "#e2e8f0", whiteSpace: "normal", wordBreak: "break-all", maxWidth: 360 }}
                wrapperStyle={{ maxWidth: 360, overflow: 'hidden' }}
                cursor={{ fill: "rgba(59,130,246,0.1)" }}
              />
              <Bar dataKey="count" name="UTXO" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={yData.length <= 3 ? 40 : yData.length <= 5 ? 28 : 18}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>
    </div>
  );
}
